/**
 * Spotify sign-in: authorisation code + PKCE, and the token store behind it.
 *
 * This is the fourth copy of the shape first built for Google and then for
 * OneDrive and Dropbox, and dropboxAuth.ts is the one to read alongside it -
 * the two are deliberately near-identical, down to the section headings.
 * What differs is Spotify's:
 *
 * - `S256` is the only challenge method Spotify accepts. dropboxAuth falls
 *   back to `plain` where SubtleCrypto is missing; here that would just be a
 *   slower way of failing, so a browser without crypto.subtle is told so.
 * - No `token_access_type=offline`. A PKCE exchange always returns a refresh
 *   token, and a refresh may or may not return a new one - keep the old one
 *   when it does not.
 * - Error responses are `{ error, error_description }`, never
 *   `error_summary`.
 *
 * Why this exists at all: Spotify removed the implicit grant on 27 November
 * 2025. The old flow here was `response_type=token` with a hidden iframe for
 * silent renewal, and it stopped working that day. On top of that it was a
 * popup-and-postMessage flow, which iOS throws away for the reasons
 * MOBILE-IOS.md section 1c spells out - so the redirect path fixes two things
 * at once, exactly as it did for Dropbox.
 */

import { shouldUseRedirect } from '../../utilities/platform';
import { hasUserActivation, popupBlockedMessage } from '../../utilities/popupBlocked';
import { spotifyClientId } from '../../config';

export const clientID = spotifyClientId;

const authEndpoint = 'https://accounts.spotify.com/authorize';
const tokenEndpoint = 'https://accounts.spotify.com/api/token';

/**
 * https://developer.spotify.com/documentation/web-api/concepts/scopes
 *
 * `streaming` is the Web Playback SDK, and the SDK's own documentation still
 * lists user-read-email and user-read-private beside it, so they stay even
 * though the February 2026 Development Mode changes stripped `email`,
 * `country` and `product` out of /me. The playlist-modify and library-modify
 * scopes that used to be requested here were never used by anything and are
 * gone: each one was a line on the consent screen buying nothing.
 */
export const scopes = [
  'streaming',
  'user-modify-playback-state',
  'user-read-email',
  'user-read-private',
  'playlist-read-private',
  'playlist-read-collaborative',
];

/**
 * Must match a redirect URI registered in the Spotify developer dashboard
 * byte for byte, for every origin the app is served from, localhost
 * included. Spotify's docs say "localhost is
 * not allowed", but the dashboard takes the HTTPS form without a murmur - it
 * is the plain-http form it flags as insecure.
 */
export const redirectPath = '/spotify-redirect';

export function redirectUri() {
  return window.location.origin + redirectPath;
}

export interface SpotifyTokens {
  accessToken: string;
  refreshToken?: string;
  /** Epoch ms. */
  expiresAt?: number;
}

/**
 * The single field the popup posts back to its opener.
 *
 * It carries no token. The popup redeems the code itself and writes the token
 * store, which the opener shares with it - same origin - so all the opener
 * needs to hear is that it is worth looking again.
 */
export const popupDoneMessage = 'fadilaSpotifyAuthComplete';

const tokensKey = 'spotifyAuth';
const pendingKey = 'spotifyAuthPending';

/** A round trip that has taken longer than this is not one we still expect. */
const pendingLifetimeMs = 10 * 60 * 1000;

interface PendingAuth {
  state: string;
  verifier: string;
  returnTo: string;
  createdAt: number;
}

/* -------------------------------------------------------------------------
 * Token store
 * ---------------------------------------------------------------------- */

export function readTokens(): SpotifyTokens | undefined {
  try {
    const raw = localStorage.getItem(tokensKey);

    if (!raw) {
      return undefined;
    }

    const parsed = JSON.parse(raw) as SpotifyTokens;
    return parsed && parsed.accessToken ? parsed : undefined;
  } catch {
    return undefined;
  }
}

export function storeTokens(tokens: SpotifyTokens) {
  try {
    localStorage.setItem(tokensKey, JSON.stringify(tokens));
  } catch {
    // Private browsing. The token still works for this page's lifetime.
  }
}

export function clearTokens() {
  try {
    localStorage.removeItem(tokensKey);
  } catch {}
}

/* -------------------------------------------------------------------------
 * PKCE
 * ---------------------------------------------------------------------- */

function randomUrlSafe(byteLength: number) {
  const bytes = new Uint8Array(byteLength);

  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  return Array.prototype.map.call(bytes, (b: number) => ('0' + b.toString(16)).slice(-2)).join('');
}

function base64UrlEncode(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';

  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * S256, and only S256 - Spotify does not accept `plain`.
 *
 * crypto.subtle needs a secure context. Everything this app runs in is one -
 * production is HTTPS and the dev server is forced to HTTPS by .env - so this
 * should never throw. If it does, the message says what is missing rather
 * than letting Spotify answer with an invalid_request nobody can act on.
 */
async function deriveChallenge(verifier: string) {
  if (!window.crypto || !window.crypto.subtle) {
    throw new Error(
      'Spotify sign-in needs Web Crypto, which this browser does not provide here. ' +
        'Open Fadila over HTTPS and try again.',
    );
  }

  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));

  return base64UrlEncode(digest);
}

/* -------------------------------------------------------------------------
 * Starting the flow
 * ---------------------------------------------------------------------- */

function readPending(): PendingAuth | undefined {
  try {
    const raw = localStorage.getItem(pendingKey);

    if (!raw) {
      return undefined;
    }

    const parsed = JSON.parse(raw) as PendingAuth;

    if (!parsed || !parsed.state || Date.now() - parsed.createdAt > pendingLifetimeMs) {
      return undefined;
    }

    return parsed;
  } catch {
    return undefined;
  }
}

function clearPending() {
  try {
    localStorage.removeItem(pendingKey);
  } catch {}
}

/**
 * localStorage rather than sessionStorage, for the same reason gisRedirect
 * gives: iOS may service the cross-origin hop by suspending or even killing
 * the standalone app, and a relaunch starts a fresh session - which would
 * strand the flow at exactly the point it is meant to fix.
 */
function buildAuthUrl() {
  const state = randomUrlSafe(16);
  // 32 bytes hex-encoded is 64 characters, inside Spotify's 43-128 range, and
  // hex digits are within its allowed alphabet.
  const verifier = randomUrlSafe(32);

  try {
    localStorage.setItem(
      pendingKey,
      JSON.stringify({
        state,
        verifier,
        // Come back to wherever the user actually was, not a fixed landing page.
        returnTo: window.location.pathname + window.location.search,
        createdAt: Date.now(),
      } as PendingAuth),
    );
  } catch {
    // Private browsing. The trip out still works; the trip back will fail the
    // state check and say so, which beats navigating away silently.
  }

  return deriveChallenge(verifier).then(function buildUrl(challenge) {
    const params = [
      ['client_id', clientID],
      ['redirect_uri', redirectUri()],
      ['response_type', 'code'],
      ['state', state],
      ['scope', scopes.join(' ')],
      ['code_challenge', challenge],
      ['code_challenge_method', 'S256'],
    ]
      .map((pair) => encodeURIComponent(pair[0]) + '=' + encodeURIComponent(pair[1]))
      .join('&');

    return authEndpoint + '?' + params;
  });
}

/**
 * Whether this device needs the whole page to go to Spotify.
 *
 * Exported so callers can tell an authorize() that will return from one that
 * navigates away and never returns at all.
 */
export function willRedirect(allowRedirect?: boolean) {
  return !!allowRedirect && shouldUseRedirect();
}

/**
 * Navigates away. Nothing after this call runs, so the promise a caller is
 * holding never settles - by design, exactly as beginRedirect does for Google.
 */
export async function beginRedirect() {
  const authUrl = await buildAuthUrl();

  // assign, not replace: leaves the app in history so back does something sane.
  window.location.assign(authUrl);
}

/**
 * The desktop path. Returns the popup so the caller can watch it close.
 *
 * The popup is opened on about:blank *synchronously*, before the PKCE
 * challenge is derived, and pointed at Spotify afterwards. Deriving the
 * challenge is async - crypto.subtle.digest returns a promise - and calling
 * window.open after that await is the very mistake the Dropbox rewrite was
 * about. Opening early and navigating late keeps the call inside the user
 * gesture, where the popup blocker still allows it.
 */
export function beginPopup() {
  // Read before the window.open that is about to fail: afterwards there is no
  // telling whether a click was vouching for this pop-up.
  const hadGesture = hasUserActivation();

  const popup = window.open(
    'about:blank',
    'SpotifyAuthPopup',
    'width=670,height=800,dialog=yes,dependent=yes,scrollbars=yes,location=yes',
  );

  if (!popup) {
    return Promise.reject(new Error(popupBlockedMessage('Spotify', hadGesture)));
  }

  return buildAuthUrl().then(
    function pointPopupAtSpotify(authUrl) {
      popup.location.href = authUrl;
      return popup;
    },
    function closePopupOnFailure(err) {
      popup.close();
      throw err;
    },
  );
}

/* -------------------------------------------------------------------------
 * Finishing the flow
 * ---------------------------------------------------------------------- */

export class SpotifyAuthError extends Error {
  constructor(
    message: string,
    public readonly returnTo: string,
  ) {
    super(message);
    this.name = 'SpotifyAuthError';
  }
}

async function postForm(body: string[][]) {
  const res = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body
      .map((pair) => encodeURIComponent(pair[0]) + '=' + encodeURIComponent(pair[1]))
      .join('&'),
  });

  const text = await res.text();
  let parsed: any;

  try {
    parsed = JSON.parse(text);
  } catch {
    parsed = undefined;
  }

  if (!res.ok) {
    throw new Error(
      (parsed && (parsed.error_description || parsed.error)) ||
        'Spotify rejected the token request (' + res.status + ').',
    );
  }

  return parsed;
}

function parseParams(str: string) {
  const out: { [key: string]: string } = {};

  str
    .replace(/^[#?]/, '')
    .split('&')
    .forEach(function readPair(pair) {
      if (!pair) {
        return;
      }

      const idx = pair.indexOf('=');
      const key = decodeURIComponent(idx === -1 ? pair : pair.slice(0, idx));

      out[key] = idx === -1 ? '' : decodeURIComponent(pair.slice(idx + 1).replace(/\+/g, ' '));
    });

  return out;
}

export interface SpotifyAuthResult {
  returnTo: string;
}

/**
 * Exchanges the code Spotify handed back and stores the tokens.
 *
 * Runs on /spotify-redirect - which is either the popup or, on iOS, the app
 * itself mid-navigation. Doing the exchange in one place rather than two is
 * why the popup posts a bare "done" to its opener instead of posting a code
 * for the opener to redeem.
 */
export async function completeAuth(search: string): Promise<SpotifyAuthResult> {
  const pending = readPending();
  const returnTo = (pending && pending.returnTo) || '/app';

  clearPending();

  const params = parseParams(search);

  if (params.error) {
    throw new SpotifyAuthError(params.error_description || params.error, returnTo);
  }

  if (!pending) {
    throw new SpotifyAuthError(
      'Spotify sign-in could not be completed - the request it answers has expired.',
      returnTo,
    );
  }

  if (!params.state || params.state !== pending.state) {
    throw new SpotifyAuthError('Spotify sign-in returned an unexpected response.', returnTo);
  }

  if (!params.code) {
    throw new SpotifyAuthError('Spotify sign-in returned no authorisation code.', returnTo);
  }

  let payload: any;

  try {
    payload = await postForm([
      ['grant_type', 'authorization_code'],
      ['code', params.code],
      ['redirect_uri', redirectUri()],
      ['client_id', clientID],
      ['code_verifier', pending.verifier],
    ]);
  } catch (err) {
    throw new SpotifyAuthError((err as Error).message, returnTo);
  }

  if (!payload || !payload.access_token) {
    throw new SpotifyAuthError('Spotify returned no access token.', returnTo);
  }

  storeTokens({
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token,
    expiresAt: payload.expires_in ? Date.now() + payload.expires_in * 1000 : undefined,
  });

  return { returnTo };
}

/* -------------------------------------------------------------------------
 * Keeping it alive
 * ---------------------------------------------------------------------- */

/** Renew this far ahead of expiry, so a request in flight does not age out. */
const renewMarginMs = 5 * 60 * 1000;

/**
 * In-flight refresh, shared. Every API call and the Web Playback SDK's
 * getOAuthToken callback come through here, and the SDK asks for a token
 * whenever it feels like it - without this they would each start their own
 * refresh and race to write the result.
 */
let refreshInFlight: Promise<SpotifyTokens> | undefined;

async function refresh(tokens: SpotifyTokens): Promise<SpotifyTokens> {
  const payload = await postForm([
    ['grant_type', 'refresh_token'],
    ['refresh_token', tokens.refreshToken!],
    ['client_id', clientID],
  ]);

  if (!payload || !payload.access_token) {
    throw new Error('Spotify returned no access token when renewing.');
  }

  const renewed: SpotifyTokens = {
    accessToken: payload.access_token,
    // A refresh response might not repeat the refresh token; keep ours.
    refreshToken: payload.refresh_token || tokens.refreshToken,
    expiresAt: payload.expires_in ? Date.now() + payload.expires_in * 1000 : undefined,
  };

  storeTokens(renewed);

  return renewed;
}

/**
 * The current access token, renewed first if it is close to expiring.
 *
 * Returns undefined when there is nothing stored and nothing to renew with -
 * that is "not signed in", not an error.
 */
export function getFreshAccessToken(): Promise<string | undefined> {
  const tokens = readTokens();

  if (!tokens) {
    return Promise.resolve(undefined);
  }

  const stillGood = tokens.expiresAt === undefined || tokens.expiresAt - Date.now() > renewMarginMs;

  if (stillGood || !tokens.refreshToken) {
    return Promise.resolve(tokens.accessToken);
  }

  if (!refreshInFlight) {
    refreshInFlight = refresh(tokens).finally(function clearInFlight() {
      refreshInFlight = undefined;
    });
  }

  return refreshInFlight.then(
    (renewed) => renewed.accessToken,
    function useStaleTokenAndLet401Decide() {
      // A failed renewal is not fatal here. Returning the stale token lets the
      // request happen and fail with a 401, which spotifyService's error
      // handler already turns into a logout - one place that decides that,
      // rather than two that disagree.
      return tokens.accessToken;
    },
  );
}
